import { Fragment } from 'react'
import type { TmsLoadRow } from '@numeo/types-main-web-app'
import { DocumentChips } from './DocumentChips'
import { formatUsd, laneOf, LOAD_STAGE_LABEL } from './format'
import { LoadFixActions } from './LoadFixActions'
import { documentActions } from './rowActions'
import { InvoiceCell } from './threadInvoices'

interface LoadsTableProps {
    rows: TmsLoadRow[]
    onSend: (message: string) => void
}

/**
 * Several loads (`tms_loads.list`) as one table. When the list holds delivered loads it also shows each one's invoice and documents,
 * and under a load missing its rate con or POD, the upload that fixes it, right here: what blocks invoicing is seen and fixed in the
 * one place the loads are listed.
 */
export const LoadsTable = ({ rows, onSend }: LoadsTableProps) => {
    const delivered = rows.some((row) => row.status === 'delivered')
    const columns = delivered ? 7 : 5
    return (
        <div className="overflow-x-auto">
            <table className="w-full text-left text-xs">
                <thead className="text-foreground-muted">
                    <tr>
                        <th className="py-1 pr-3 font-medium">Load</th>
                        <th className="py-1 pr-3 font-medium">Status</th>
                        <th className="py-1 pr-3 font-medium">Broker</th>
                        <th className="py-1 pr-3 font-medium">Lane</th>
                        <th className="py-1 pr-3 text-right font-medium">Rate</th>
                        {delivered && <th className="py-1 pr-3 font-medium">Invoice</th>}
                        {delivered && <th className="py-1 font-medium">Documents</th>}
                    </tr>
                </thead>
                <tbody>
                    {rows.map((row) => {
                        const fixes = row.load_number ? documentActions(row.document_chips) : []
                        return (
                            <Fragment key={row.load_id}>
                                <tr className="border-t border-border">
                                    <td className="py-1.5 pr-3 font-medium">#{row.load_number ?? '—'}</td>
                                    <td className="py-1.5 pr-3">{LOAD_STAGE_LABEL[row.status] ?? row.status}</td>
                                    <td className="py-1.5 pr-3">{row.broker ?? '—'}</td>
                                    <td className="py-1.5 pr-3">{laneOf(row.origin, row.destination)}</td>
                                    <td className="py-1.5 pr-3 text-right tabular-nums">{formatUsd(row.broker_rate_usd)}</td>
                                    {delivered && (
                                        <td className="py-1.5 pr-3 whitespace-nowrap">
                                            <InvoiceCell row={row} />
                                        </td>
                                    )}
                                    {delivered && (
                                        <td className="py-1.5">
                                            <DocumentChips documents={row.document_chips} />
                                        </td>
                                    )}
                                </tr>
                                {row.load_number && fixes.length > 0 && (
                                    <tr>
                                        <td colSpan={columns} className="pb-2">
                                            {/* Zero width, full cell: a wide revision opened here scrolls in place instead of widening the table. */}
                                            <div className="w-0 min-w-full overflow-x-auto">
                                                <LoadFixActions loadNumber={row.load_number} loadId={row.load_id} actions={fixes} onSend={onSend} />
                                            </div>
                                        </td>
                                    </tr>
                                )}
                            </Fragment>
                        )
                    })}
                </tbody>
            </table>
        </div>
    )
}
